import { useState } from 'react';
import IPICalculator from './IPICalculator';
import NCCNIPICalculator from './NCCNIPICalculator';
import CNSIPICalculator from './CNSIPICalculator';
import DeauvilleCalculator from './DeauvilleCalculator';
import FLIPICalculator from './FLIPICalculator';
import PRIMAPICalculator from './PRIMAPICalculator';
import MIPICalculator from './MIPICalculator';
import MIPIcCalculator from './MIPIcCalculator';
import CLLIPICalculator from './CLLIPICalculator';
import IPSSWMCalculator from './IPSSWMCalculator';
import IELSGCalculator from './IELSGCalculator';
import PITCalculator from './PITCalculator';
import PINKCalculator from './PINKCalculator';
import TNMBCalculator from './TNMBCalculator';
import HCTCICalculator from './HCTCICalculator';
import './InlineCalculator.css';

const CALCULATOR_MAP = {
  ipi: { label: 'IPI / R-IPI Calculator', component: IPICalculator },
  nccnipi: { label: 'NCCN-IPI Calculator', component: NCCNIPICalculator },
  cnsipi: { label: 'CNS-IPI Calculator', component: CNSIPICalculator },
  deauville: { label: 'Deauville Score', component: DeauvilleCalculator },
  flipi: { label: 'FLIPI Calculator', component: FLIPICalculator },
  primapi: { label: 'PRIMA-PI Calculator', component: PRIMAPICalculator },
  mipi: { label: 'MIPI Calculator', component: MIPICalculator },
  mipic: { label: 'MIPI-c Calculator', component: MIPIcCalculator },
  cllipi: { label: 'CLL-IPI Calculator', component: CLLIPICalculator },
  ipsswm: { label: 'IPSSWM Calculator', component: IPSSWMCalculator },
  ielsg: { label: 'IELSG Score', component: IELSGCalculator },
  pit: { label: 'PIT Calculator', component: PITCalculator },
  pink: { label: 'PINK / PINK-E Score', component: PINKCalculator },
  tnmb: { label: 'TNMB Staging (MF/SS)', component: TNMBCalculator },
  hctci: { label: 'HCT-CI Calculator', component: HCTCICalculator },
};

// Maps '<guidelineId>:<nodeId>' to relevant calculators.
// Node IDs are only unique WITHIN a guideline (e.g. 'dec-response' exists in dlbcl, burkitt and
// pmbcl; 'resp-eot-pet' exists in burkitt, hl, mcl and pmbcl), so keys must be namespaced.
const NODE_CALCULATORS = {
  // DLBCL nodes
  'dlbcl:wu-risk': ['ipi', 'nccnipi'],
  'dlbcl:dec-stage': ['ipi', 'nccnipi'],
  'dlbcl:dec-cns': ['cnsipi'],
  'dlbcl:resp-pet': ['deauville'],
  'dlbcl:dec-response': ['deauville'],
  'dlbcl:dec-transplant': ['hctci'],
  // FL nodes
  'fl:wu-risk-fl': ['flipi', 'primapi'],
  'fl:dec-stage-fl': ['flipi'],
  'fl:dec-treat-now': ['flipi', 'primapi'],
  'fl:resp-eot-fl': ['deauville'],
  'fl:dec-response-fl': ['deauville'],
  // MCL nodes
  'mcl:wu-mipi': ['mipi', 'mipic'],
  'mcl:dec-fitness': ['mipi', 'mipic'],
  'mcl:resp-eot-pet': ['deauville'],
  'mcl:dec-response-mcl': ['deauville'],
  // CLL nodes
  'cll:wu-risk-stratify': ['cllipi'],
  'cll:dec-ighv': ['cllipi'],
  'cll:dec-need-treatment': ['cllipi'],
  'cll:dec-allosct': ['hctci'],
  // Burkitt nodes
  'burkitt:resp-eot-pet': ['deauville'],
  'burkitt:dec-response': ['deauville'],
  // PMBCL nodes
  'pmbcl:resp-eot-pet': ['deauville'],
  'pmbcl:resp-eot-pet-tx': ['deauville'],
  'pmbcl:dec-pet-daepochr': ['deauville'],
  // HL nodes
  'hl:resp-eot-pet': ['deauville'],
  'hl:dec-interim-pet-adv': ['deauville'],
  'hl:dec-eot-response': ['deauville'],
  // HIV-BCL nodes (reuses IPI, Deauville, HCT-CI)
  'hiv-bcl:wu-hiv-status': ['ipi'],
  'hiv-bcl:dec-hivbcl-subtype': ['ipi'],
  'hiv-bcl:resp-hiv-pet': ['deauville'],
  'hiv-bcl:dec-hiv-response': ['deauville'],
  'hiv-bcl:dec-hiv-transplant': ['hctci'],
  // PTLD nodes (reuses Deauville, HCT-CI)
  'ptld:resp-ptld-pet': ['deauville'],
  'ptld:dec-ptld-rr-transplant': ['hctci'],
  // HGBL nodes (reuses IPI, CNS-IPI, Deauville)
  'hgbl:wu-hgbl-ipi': ['ipi', 'cnsipi'],
  'hgbl:dec-hgbl-cns-involvement': ['cnsipi'],
  'hgbl:resp-hgbl-pet': ['deauville'],
  'hgbl:dec-hgbl-response': ['deauville'],
  // HT-DLBCL nodes (reuses Deauville, HCT-CI)
  'ht-dlbcl:resp-ht-pet': ['deauville'],
  'ht-dlbcl:dec-ht-response': ['deauville'],
  'ht-dlbcl:dec-ht-rr-transplant': ['hctci'],
  // WM nodes (IPSSWM)
  'wm:wu-ipsswm': ['ipsswm'],
  'wm:dec-ipsswm-score': ['ipsswm'],
  'wm:dec-treat-wm': ['ipsswm'],
  // PCNSL nodes (IELSG)
  'pcnsl:wu-ielsg-pcnsl': ['ielsg'],
  'pcnsl:dec-fitness-pcnsl': ['ielsg'],
  // PTCL nodes (PIT, Deauville)
  'ptcl:wu-pit': ['pit'],
  'ptcl:dec-cd30-status': ['pit'],
  'ptcl:dec-fitness-ptcl': ['pit'],
  'ptcl:dec-response-ptcl': ['deauville'],
  // ATLL nodes (Deauville)
  'atll:dec-response-atll': ['deauville'],
  // ENKTL nodes (PINK)
  'enktl:wu-pink-score': ['pink'],
  'enktl:dec-stage-enktl': ['pink'],
  'enktl:dec-response-localized': ['deauville'],
  'enktl:dec-response-advanced': ['deauville'],
  // MF/SS nodes (TNMB)
  'mf-ss:wu-staging-mfss': ['tnmb'],
  'mf-ss:dec-tnmb-mfss': ['tnmb'],
  'mf-ss:dec-blood-mfss': ['tnmb'],
};

const EMPTY = [];

// Preferred call: getCalculatorsForNode(guidelineId, nodeId).
// Legacy single-argument call (node id only) is still supported: it scans for a '*:<nodeId>' match
// and answers only when every guideline that defines the node maps it to the same calculators.
// Ambiguous node ids return [] — those callers must be updated to pass the guideline id.
export function getCalculatorsForNode(guidelineIdOrNodeId, maybeNodeId) {
  if (!guidelineIdOrNodeId) return EMPTY;

  if (maybeNodeId) {
    return NODE_CALCULATORS[`${guidelineIdOrNodeId}:${maybeNodeId}`] || EMPTY;
  }

  if (guidelineIdOrNodeId.includes(':')) {
    return NODE_CALCULATORS[guidelineIdOrNodeId] || EMPTY;
  }

  const suffix = `:${guidelineIdOrNodeId}`;
  let match = null;
  for (const key of Object.keys(NODE_CALCULATORS)) {
    if (!key.endsWith(suffix)) continue;
    const value = NODE_CALCULATORS[key];
    if (match === null) {
      match = value;
    } else if (match.join(',') !== value.join(',')) {
      return EMPTY;
    }
  }
  return match || EMPTY;
}

function InlineCalculator({ calculatorIds }) {
  const [openCalc, setOpenCalc] = useState(null);

  if (!calculatorIds || calculatorIds.length === 0) return null;

  return (
    <div className="inline-calculators">
      <div className="inline-calc-header">
        <span className="inline-calc-icon">🧮</span>
        <span>Relevant calculators:</span>
      </div>
      <div className="inline-calc-buttons">
        {calculatorIds.map((id) => {
          const calc = CALCULATOR_MAP[id];
          if (!calc) return null;
          return (
            <button
              key={id}
              className={`inline-calc-toggle ${openCalc === id ? 'active' : ''}`}
              onClick={() => setOpenCalc(openCalc === id ? null : id)}
            >
              {calc.label}
              <span className="inline-calc-chevron">{openCalc === id ? '▲' : '▼'}</span>
            </button>
          );
        })}
      </div>
      {openCalc && CALCULATOR_MAP[openCalc] && (
        <div className="inline-calc-body">
          {(() => {
            const Comp = CALCULATOR_MAP[openCalc].component;
            return <Comp />;
          })()}
        </div>
      )}
    </div>
  );
}

export default InlineCalculator;
